import { formatNumber, todayISOChile } from '../styles/tokens/formatters.js';
import { supabase } from './supabase/supabaseClient.js';

export const getUfDiaria = async () => {
  try {
    const { data, error } = await supabase
      .from('uf_diaria')
      .select('*')
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error al obtener UF diaria:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error al obtener UF diaria:', error);
    throw error;
  }
};

export const getUfDiariaById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('uf_diaria')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error al obtener UF diaria con ID ${id}:`, error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error(`Error al obtener UF diaria con ID ${id}:`, error);
    throw error;
  }
};

export const createUfDiaria = async (ufDiariaData) => {
  try {
    const { data, error } = await supabase
      .from('uf_diaria')
      .insert([{
        fecha: ufDiariaData.fecha,
        valor_clp: ufDiariaData.valor_clp || ufDiariaData.valor,
        fuente: ufDiariaData.fuente || 'Manual'
      }])
      .select()
      .single();

    if (error) {
      console.error('Error al crear UF diaria:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al crear UF diaria:', error);
    throw error;
  }
};

export const updateUfDiaria = async (id, ufDiariaData) => {
  try {
    const updateData = {};
    
    if (ufDiariaData.fecha !== undefined) updateData.fecha = ufDiariaData.fecha;
    if (ufDiariaData.valor_clp !== undefined) updateData.valor_clp = ufDiariaData.valor_clp;
    if (ufDiariaData.valor !== undefined) updateData.valor_clp = ufDiariaData.valor;
    if (ufDiariaData.fuente !== undefined) updateData.fuente = ufDiariaData.fuente;
    
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('uf_diaria')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error al actualizar UF diaria con ID ${id}:`, error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error(`Error al actualizar UF diaria con ID ${id}:`, error);
    throw error;
  }
};

export const deleteUfDiaria = async (id) => {
  try {
    const { error } = await supabase
      .from('uf_diaria')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error al eliminar UF diaria con ID ${id}:`, error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error(`Error al eliminar UF diaria con ID ${id}:`, error);
    throw error;
  }
};

// Función para obtener la última fecha de UF disponible en la base de datos
export const getLastUfDate = async () => {
  try {
    const { data, error } = await supabase
      .from('uf_diaria')
      .select('fecha')
      .order('fecha', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error al obtener última fecha UF:', error);
      return null;
    }

    return data && data.length > 0 ? data[0].fecha : null;
  } catch (error) {
    console.error('Error al obtener última fecha UF:', error);
    return null;
  }
};

// Valor UF vigente: la fecha más reciente que no sea futura. Se busca <= hoy
// (y no la fecha exacta) porque uf_diaria puede tener huecos de fin de semana o
// adelantar valores publicados. Devuelve null si no hay ninguna fila utilizable
// — el llamador debe mostrar el monto en UF sin conversión, nunca asumir una
// tasa por defecto.
export const getValorUfVigente = async () => {
  const hoy = todayISOChile();

  const { data, error } = await supabase
    .from('uf_diaria')
    .select('fecha, valor_clp')
    .lte('fecha', hoy)
    .order('fecha', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error al obtener UF vigente:', error);
    return null;
  }

  if (!data) return null;

  return { fecha: data.fecha, valorClp: Number(data.valor_clp) };
};

// Función para sincronizar con la API de CMF Chile
// Función para parsear números en formato chileno de la API de CMF
const parseChileanNumber = (valorString) => {
  if (!valorString) return 0;
  // La API de CMF devuelve valores como "39.394,46" (formato chileno)
  // Quitar puntos de miles y cambiar coma por punto decimal
  return parseFloat(valorString.replace(/\./g, '').replace(',', '.'));
};

// Función para formatear fecha sin problemas de zona horaria
const formatDateForDB = (dateString) => {
  if (!dateString) return null;
  // Asegurar que la fecha se mantenga exactamente como viene de la API
  // sin conversión de zona horaria que cause desfase de días
  return dateString; // La API ya devuelve en formato YYYY-MM-DD
};

// Función para sincronizar con la API de CMF Chile
export const syncUfFromBancoCentral = async (fechaInicio = null, fechaFin = null) => {
  try {
    // Si no se proporciona fechaInicio, obtener la última fecha disponible en la BD
    if (!fechaInicio) {
      const lastDate = await getLastUfDate();
      if (lastDate) {
        // Usar la última fecha disponible directamente
        // La API /posteriores devuelve fechas POSTERIORES a la fecha especificada
        fechaInicio = lastDate;
      } else {
        // Si no hay datos, comenzar desde hace 30 días
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setUTCDate(thirtyDaysAgo.getUTCDate() - 30);
        fechaInicio = thirtyDaysAgo.toISOString().split('T')[0];
      }
    }



    // Usar el endpoint de fechas posteriores según la documentación
    const [year, month, day] = fechaInicio.split('-');
    
    // Construir URL usando el endpoint /posteriores según la documentación
    const apiUrl = `https://api.cmfchile.cl/api-sbifv3/recursos_api/uf/posteriores/${year}/${month}/dias/${day}?apikey=${process.env.REACT_APP_CMF_API_KEY}&formato=json`;
    
    console.log('Sincronizando UF desde CMF:', { 
      fechaInicio, 
      fechaFin: fechaFin || 'sin límite (todas las fechas disponibles)',
      year, 
      month, 
      day,
      apiUrl: apiUrl.replace(process.env.REACT_APP_CMF_API_KEY, 'API_KEY_HIDDEN') 
    });

    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`Error en la API de CMF: ${response.status} - ${response.statusText}`);
    }
    
    const cmfData = await response.json();
    
    if (!cmfData || !cmfData.UFs || !Array.isArray(cmfData.UFs)) {
      throw new Error('Respuesta inválida de la API de CMF');
    }

    // Procesar los datos de la API usando funciones centralizadas
    let ufData = cmfData.UFs.map(item => {
      return {
        fecha: formatDateForDB(item.Fecha), // Mantener fecha exacta sin conversión
        valor_clp: parseChileanNumber(item.Valor), // Usar función centralizada
        fuente: 'CMF Chile'
      };
    });

    console.log('Datos procesados de CMF:', {
      totalRecords: ufData.length,
      firstRecord: ufData[0],
      lastRecord: ufData[ufData.length - 1],
      fechaInicio,
      fechaFin: fechaFin || 'sin límite'
    });

    // Solo filtrar por fechaFin si se proporciona explícitamente
    if (fechaFin) {
      const originalLength = ufData.length;
      ufData = ufData.filter(item => {
        // Comparar fechas como strings para evitar problemas de zona horaria
        return item.fecha >= fechaInicio && item.fecha <= fechaFin;
      });
      console.log(`Filtrado por rango de fechas: ${originalLength} -> ${ufData.length} registros`);
    } else {
      // Si no se especifica fechaFin, solo filtrar que sea >= fechaInicio
      const originalLength = ufData.length;
      ufData = ufData.filter(item => {
        return item.fecha >= fechaInicio;
      });
      console.log(`Filtrado desde fecha inicio: ${originalLength} -> ${ufData.length} registros`);
    }
    
    if (ufData.length === 0) {
      return {
        success: true,
        recordsProcessed: 0,
        recordsInserted: 0,
        source: 'CMF Chile',
        message: 'No se encontraron datos para el período solicitado'
      };
    }

    // Insertar datos en Supabase - cambiar estrategia para evitar duplicados
    let recordsInserted = 0;
    
    for (const ufItem of ufData) {
      try {
        // Verificar si ya existe la fecha
        const { data: existing, error: checkError } = await supabase
          .from('uf_diaria')
          .select('id')
          .eq('fecha', ufItem.fecha)
          .single();

        if (checkError && checkError.code !== 'PGRST116') {
          // Error diferente a "no encontrado"
          console.error('Error al verificar fecha existente:', checkError);
          continue;
        }

        if (existing) {
          // Actualizar registro existente
          const { error: updateError } = await supabase
            .from('uf_diaria')
            .update({
              valor_clp: ufItem.valor_clp,
              fuente: ufItem.fuente,
              updated_at: new Date().toISOString()
            })
            .eq('id', existing.id);

          if (!updateError) {
            recordsInserted++;
          }
        } else {
          // Insertar nuevo registro
          const { error: insertError } = await supabase
            .from('uf_diaria')
            .insert([ufItem]);

          if (!insertError) {
            recordsInserted++;
          }
        }
      } catch (itemError) {
        console.error('Error procesando item UF:', itemError);
        continue;
      }
    }
    
    return {
      success: true,
      recordsProcessed: ufData.length,
      recordsInserted: recordsInserted,
      source: 'CMF Chile',
      fechaInicio,
      fechaFin
    };
    
  } catch (error) {
    console.error('Error al sincronizar con API de CMF:', error);
    
    // Proporcionar mensajes de error más específicos
    let errorMessage = error.message;
    if (error.message.includes('API de CMF')) {
      errorMessage += '. Verifique la variable de entorno REACT_APP_CMF_API_KEY';
    } else if (error.message.includes('expected pattern')) {
      errorMessage = 'Error en el formato de fechas para la API de CMF. ' + error.message;
    }
    
    throw new Error(errorMessage);
  }
};
